/**
 * panelAtoms - Persistent panel layout state for the Graph Explorer.
 *
 * Uses `atomWithStorage` from jotai/utils so that panel expand/collapse
 * state and dimensions survive page reloads via localStorage.
 */

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Jotai RESET is a `unique symbol`; atom generics need `typeof RESET`, which requires a value binding
import { atomWithStorage, RESET } from 'jotai/utils';

// ============================================================================
// Constants
// ============================================================================

export const DEFAULT_LEFT_PANEL_WIDTH = 320;
export const MIN_PANEL_WIDTH = 280;
export const MAX_PANEL_WIDTH = 800;
export const DEFAULT_BOTTOM_PANEL_HEIGHT = 200;

/** Duration (ms) of panel slide/reveal CSS transitions. */
export const TRANSITION_MS = 300;

// ============================================================================
// Persisted panel atoms (backed by localStorage)
// ============================================================================

export const LeftPanelExpandedAtom: WritableAtom<
	boolean,
	[boolean | typeof RESET | ((prev: boolean) => boolean | typeof RESET)],
	void
> = atomWithStorage('graph-explorer-left-panel-expanded', true);

export const LeftPanelWidthAtom: WritableAtom<
	number,
	[number | typeof RESET | ((prev: number) => number | typeof RESET)],
	void
> = atomWithStorage('graph-explorer-left-panel-width', DEFAULT_LEFT_PANEL_WIDTH);

export const RightPanelExpandedAtom: WritableAtom<
	boolean,
	[boolean | typeof RESET | ((prev: boolean) => boolean | typeof RESET)],
	void
> = atomWithStorage('graph-explorer-right-panel-expanded', true);

/**
 * Viewport height threshold (px) below which the Cypher editor bottom panel
 * starts collapsed to preserve space for the explore landing prompts.
 */
export const SMALL_VIEWPORT_HEIGHT_THRESHOLD = 980;

export const CypherPanelExpandedAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(
	typeof window !== 'undefined' ? window.innerHeight >= SMALL_VIEWPORT_HEIGHT_THRESHOLD : true,
);

export const BottomPanelHeightAtom: WritableAtom<
	number,
	[number | typeof RESET | ((prev: number) => number | typeof RESET)],
	void
> = atomWithStorage('graph-explorer-bottom-panel-height', DEFAULT_BOTTOM_PANEL_HEIGHT);

// ============================================================================
// Focus mode entry tracking
// ============================================================================

/**
 * Monotonically increasing counter, incremented each time focus mode is entered.
 * Used to suppress the auto-open of the right panel during focus entry without
 * relying on synchronous ref mutations or setTimeout-based flag clearing.
 */
export const FocusModeEntryIdAtom: PrimitiveAtom<number> & {
	init: number;
} = atom<number>(0);
